/**
 * URSSAF Declaration Controller (EPIC-020 / 20.3 — §10).
 *
 * Page : `/urssaf-declaration.html`. Pilote l'intégralité du cycle de
 * déclaration mensuelle ou trimestrielle :
 *   - chargement de la déclaration courante (GET …/current)
 *   - ajustement manuel par catégorie fiscale (PATCH …/manual-adjustment)
 *     avec mémorisation du brouillon dans sessionStorage
 *   - estimation indicative des cotisations (POST /urssaf/estimation —
 *     calcul interne en mode assisté V1, cf. EPIC-099.88)
 *   - déclaration assistée V1 : téléchargement du récap PDF
 *     (GET …/pdf), report manuel sur autoentrepreneur.urssaf.fr, puis
 *     saisie de l'accusé `{ receiptNumber, receiptDate }` (POST …/submit).
 *     Pas d'appel API050 tant que l'agrément URSSAF n'est pas obtenu.
 *   - historique paginé (GET /urssaf/declarations)
 *
 * EPIC-099 / 99.37 — le télépaiement (API060) et les mandats SEPA URSSAF
 * (API070/071/075) sont hors périmètre Notamicro : le ME règle ses
 * cotisations directement sur autoentrepreneur.urssaf.fr.
 *
 * Conventions :
 *   - aucun HTML inline en JS : `<template>` + `data-slot` (cf.
 *     `utils/template.js`)
 *   - état d'ajustement persisté en sessionStorage sous la clé
 *     `nm.urssaf.adjustment.<orgId>.<declarationId>.<category>`
 *   - les erreurs 503/409/423/422 ont un rendu UI dédié (cf. §10 specs)
 *   - le badge ACRE provient du retour API040 (`acreApplied: true` →
 *     `acreAppliedRate` affiché). Notamicro n'invente jamais le taux.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

const CATEGORY_LABELS = {
  bicVente: "BIC vente de marchandises",
  bicServices: "BIC prestations de services",
  bnc: "BNC professions libérales",
};

const CATEGORY_ORDER = ["bicVente", "bicServices", "bnc"];

const SUBMISSION_STATUS_LABELS = {
  draft: "brouillon",
  submitted: "déclarée",
  failed: "échec",
};

const HISTORY_PAGE_SIZE = 10;

export default class UrssafDeclarationController extends BaseController {
  static targets = [
    "crumbPeriod",
    "periodOverline",
    "periodTitle",
    "periodSub",
    "prevPeriodButton",
    "nextPeriodButton",

    "errorBanner",
    "errorBannerTitle",
    "errorBannerMessage",

    "manualEntriesAlert",
    "manualEntriesCount",
    "manualEntriesPeriod",
    "manualEntriesConfirmLink",

    "caTableBody",
    "caTotalDeclarable",
    "adjustmentForm",
    "adjustmentCategoryLabel",
    "adjustmentAmountInput",
    "adjustmentNoteInput",
    "adjustmentNoteError",
    "saveAdjustmentButton",

    "estimationEmptyState",
    "estimationFilledState",
    "estimationTotalDisplay",
    "estimationTimestamp",
    "acreBadge",
    "acreBadgeLabel",
    "estimationTableBody",
    "estimationTableTotal",
    "estimateButton",

    "submitDraftState",
    "submitDoneState",
    "recapDownloadLink",
    "receiptNumberInput",
    "receiptDateInput",
    "saveReceiptButton",
    "receiptError",
    "receiptTimestamp",
    "receiptNumber",
    "receiptCaTotal",
    "receiptCotisations",
    "receiptVaultLink",

    "historyTableBody",
    "historyPagination",
    "historyPageInfo",
    "historyPagesContainer",
  ];

  connect() {
    this._declaration = null;
    this._estimation = null;
    this._editingCategory = null;
    this._historyPage = 1;
    this._historyTotalPages = 1;
    this._submitInFlight = false;

    this._noteInputHandler = () => this._clearAdjustmentNoteError();
    this.adjustmentNoteInputTarget.addEventListener("input", this._noteInputHandler);

    this.loadCurrent();
    this.loadHistory(1);
  }

  disconnect() {
    if (this._noteInputHandler && this.hasAdjustmentNoteInputTarget) {
      this.adjustmentNoteInputTarget.removeEventListener("input", this._noteInputHandler);
    }
  }

  // ---------------------------------------------------------------------------
  // Période courante
  // ---------------------------------------------------------------------------

  async loadCurrent() {
    this._hideErrorBanner();
    try {
      const response = await api.urssaf.getCurrent();
      this._declaration = response.declaration ?? response;
      this._renderPeriodHeader();
      this._renderManualEntriesAlert();
      this._renderCaTable();
      this._renderSubmissionState();
    } catch (error) {
      this._handleLoadError(error);
    }
  }

  _renderPeriodHeader() {
    const d = this._declaration;
    const periodLabel = this._formatPeriodLabel(d);
    this.crumbPeriodTarget.textContent = periodLabel;
    this.periodTitleTarget.textContent = periodLabel;
    this.periodOverlineTarget.textContent = d.periodType === "trimestrielle"
      ? "Déclaration trimestrielle URSSAF"
      : "Déclaration mensuelle URSSAF";

    const start = this._formatLongDate(d.periodStartDate);
    const end = this._formatLongDate(d.periodEndDate);
    let sub = `Période du ${start} au ${end}`;
    if (d.submissionDeadline) {
      sub += ` · échéance de télédéclaration le ${this._formatLongDate(d.submissionDeadline)}`;
    }
    this.periodSubTarget.textContent = sub;
  }

  _renderManualEntriesAlert() {
    const count = this._declaration.unconfirmedManualEntriesCount ?? 0;
    if (count > 0) {
      this.manualEntriesCountTarget.textContent = String(count);
      this.manualEntriesPeriodTarget.textContent = this._formatPeriodLabel(this._declaration);
      this.manualEntriesAlertTarget.hidden = false;
      // EPIC-099 / 99.27 — pointe vers la page mois détail du premier mois
      // de la période URSSAF (à défaut d'info sur le premier mois
      // *non confirmé*, le mois de début de période est le candidat
      // canonique).
      if (this.hasManualEntriesConfirmLinkTarget) {
        const startDate = this._declaration.periodStartDate;
        if (typeof startDate === "string" && /^\d{4}-\d{2}/.test(startDate)) {
          const [year, month] = startDate.split("-");
          this.manualEntriesConfirmLinkTarget.setAttribute(
            "href",
            `/livre-des-recettes-detail.html?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`,
          );
        }
      }
    } else {
      this.manualEntriesAlertTarget.hidden = true;
    }
  }

  // ---------------------------------------------------------------------------
  // CA déclarable & ajustement manuel
  // ---------------------------------------------------------------------------

  _renderCaTable() {
    const d = this._declaration;
    const caByCategory = d.caByActivityCategory ?? {};
    const adjustmentsByCategory = d.manualCaAdjustmentByCategory ?? {};
    const isSubmitted = d.submissionStatus === "submitted";

    this.caTableBodyTarget.innerHTML = "";
    let totalDeclarable = 0;

    for (const category of CATEGORY_ORDER) {
      const caRaw = caByCategory[category];
      const adjustmentRaw = adjustmentsByCategory[category];
      const isApplicable = caRaw != null;
      const ca = Number(caRaw ?? 0);
      const adjustment = Number(adjustmentRaw ?? 0);
      const total = ca + adjustment;
      if (isApplicable) totalDeclarable += total;

      const fragment = this.cloneTemplate("urssaf-ca-row-template");
      this.fillSlots(fragment, {
        category: CATEGORY_LABELS[category],
        caNotamicro: isApplicable ? this._formatEuro(ca) : "— €",
        adjustment: adjustment ? this._formatEuro(adjustment, true) : "— €",
        total: isApplicable ? this._formatEuro(total) : "— €",
      });

      const button = fragment.querySelector('button[data-slot="editButton"]');
      if (button) {
        button.removeAttribute("data-slot");
        if (!isApplicable || isSubmitted) {
          button.disabled = true;
        } else {
          button.dataset.category = category;
          button.addEventListener("click", () => this.openAdjustmentForm(category));
        }
      }

      const cells = fragment.querySelectorAll("td");
      if (!isApplicable) {
        cells.forEach((td, idx) => {
          if (idx === 1 || idx === 2 || idx === 3) td.classList.add("is-muted");
        });
      } else if (adjustment === 0) {
        if (cells[2]) cells[2].classList.add("is-muted");
      }

      this.caTableBodyTarget.appendChild(fragment);
    }

    this.caTotalDeclarableTarget.textContent = this._formatEuro(totalDeclarable);
  }

  openAdjustmentForm(category) {
    if (this._declaration?.submissionStatus === "submitted") return;
    this._editingCategory = category;
    this.adjustmentCategoryLabelTarget.textContent = CATEGORY_LABELS[category] ?? category;

    const adjustments = this._declaration.manualCaAdjustmentByCategory ?? {};
    const note = this._declaration.manualCaAdjustmentNote ?? "";

    const draft = this._readAdjustmentDraft(category);
    const amount = draft?.amount ?? adjustments[category] ?? "";
    const noteValue = draft?.note ?? note;

    this.adjustmentAmountInputTarget.value = amount === "" ? "" : this._formatAmountInput(Number(amount));
    this.adjustmentNoteInputTarget.value = noteValue;
    this._clearAdjustmentNoteError();
    this.adjustmentFormTarget.hidden = false;
    this.adjustmentAmountInputTarget.focus();
  }

  cancelAdjustment() {
    if (this._editingCategory) {
      this._clearAdjustmentDraft(this._editingCategory);
    }
    this._editingCategory = null;
    this.adjustmentAmountInputTarget.value = "";
    this.adjustmentNoteInputTarget.value = "";
    this._clearAdjustmentNoteError();
    this.adjustmentFormTarget.hidden = true;
  }

  async saveAdjustment() {
    const category = this._editingCategory;
    if (!category) return;

    const amount = this._parseAmount(this.adjustmentAmountInputTarget.value);
    const note = (this.adjustmentNoteInputTarget.value ?? "").trim();

    if (!Number.isFinite(amount)) {
      this._showAdjustmentNoteError("Le montant saisi n'est pas valide.");
      return;
    }
    if (amount > 0 && note === "") {
      this._showAdjustmentNoteError("La note est obligatoire dès lors qu'un ajustement est saisi.");
      return;
    }

    this._writeAdjustmentDraft(category, { amount, note });

    this.saveAdjustmentButtonTarget.disabled = true;
    try {
      const payload = { category, amount, note };
      const response = await api.urssaf.patchAdjustment(this._declaration.id, payload);
      this._declaration = response.declaration ?? response;
      this._clearAdjustmentDraft(category);
      this.cancelAdjustment();
      this._renderCaTable();
      this._invalidateEstimation();
      this.showSuccess("Ajustement enregistré.");
    } catch (error) {
      if (error instanceof APIError && error.status === 422) {
        this._showAdjustmentNoteError(this._extractMessage(error, "La note est obligatoire dès lors qu'un ajustement est saisi."));
      } else if (error instanceof APIError && error.status === 423) {
        this.showError("Cette période est verrouillée et ne peut plus être modifiée.");
      } else {
        this.showError(this._extractMessage(error, "Impossible d'enregistrer l'ajustement."));
      }
    } finally {
      this.saveAdjustmentButtonTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Estimation cotisations (API040)
  // ---------------------------------------------------------------------------

  async estimateCotisations() {
    this.estimateButtonTarget.disabled = true;
    try {
      const caByCategory = this._buildDeclarableCaByCategory();
      const payload = {
        caByCategory,
        periodLabel: this._declaration.periodLabel,
        periodStartDate: this._declaration.periodStartDate,
        periodEndDate: this._declaration.periodEndDate,
      };
      const response = await api.urssaf.estimate(payload);
      this._estimation = response.estimation ?? response;
      this._renderEstimation();
      this.showSuccess("Estimation URSSAF mise à jour.");
    } catch (error) {
      if (error instanceof APIError && error.status === 503) {
        this._showErrorBanner(
          "Le service URSSAF est temporairement indisponible.",
          "L'estimation n'a pas pu être récupérée. Réessayez dans quelques minutes.",
        );
      } else {
        this.showError(this._extractMessage(error, "Impossible d'obtenir l'estimation URSSAF."));
      }
    } finally {
      this.estimateButtonTarget.disabled = false;
    }
  }

  _renderEstimation() {
    const est = this._estimation;
    if (!est) {
      this.estimationEmptyStateTarget.hidden = false;
      this.estimationFilledStateTarget.hidden = true;
      return;
    }

    this.estimationEmptyStateTarget.hidden = true;
    this.estimationFilledStateTarget.hidden = false;

    const total = Number(est.totalDu ?? est.totalDueAmount ?? est.totalCotisationsAmount ?? 0);
    this.estimationTotalDisplayTarget.textContent = "";
    this.estimationTotalDisplayTarget.appendChild(
      document.createTextNode(`${this._formatEuroValue(total)} `),
    );
    const euroSpan = document.createElement("span");
    euroSpan.className = "fs-20";
    euroSpan.textContent = "€";
    this.estimationTotalDisplayTarget.appendChild(euroSpan);
    this.estimationTableTotalTarget.textContent = this._formatEuro(total);

    const ts = est.computedAt ?? new Date().toISOString();
    this.estimationTimestampTarget.setAttribute("datetime", ts);
    this.estimationTimestampTarget.textContent = this._formatLongDateTime(ts);

    const acreRate = est.acreRate ?? est.acreInternalRate ?? est.acreAppliedRate;
    if (est.acreApplied && acreRate != null) {
      const ratePct = Math.round(Number(acreRate) * 100);
      this.acreBadgeLabelTarget.textContent = `ACRE ${ratePct} %`;
      this.acreBadgeTarget.hidden = false;
    } else {
      this.acreBadgeTarget.hidden = true;
    }

    this.estimationTableBodyTarget.innerHTML = "";
    const lines = est.lines ?? [];
    for (const line of lines) {
      const fragment = this.cloneTemplate("urssaf-estimation-row-template");
      this.fillSlots(fragment, {
        label: line.label,
        base: line.base != null ? this._formatEuro(Number(line.base)) : "—",
        rate: line.rate != null ? this._formatRate(line.rate) : (line.rateLabel ?? "—"),
        amount: line.amount != null ? this._formatEuro(Number(line.amount)) : "— €",
      });
      this.estimationTableBodyTarget.appendChild(fragment);
    }
  }

  _invalidateEstimation() {
    this._estimation = null;
    this._renderEstimation();
  }

  // ---------------------------------------------------------------------------
  // Déclaration assistée V1 (EPIC-099.88) — récap PDF + saisie de l'accusé
  // ---------------------------------------------------------------------------

  validateReceipt() {
    this._hideReceiptError();
    if (this.hasSaveReceiptButtonTarget) {
      const ready = this._getReceiptNumber() !== "" && this._getReceiptDate() !== "";
      this.saveReceiptButtonTarget.disabled = !ready || this._submitInFlight;
    }
  }

  async saveReceipt() {
    if (this._submitInFlight) return;
    if (!this._declaration || this._declaration.submissionStatus === "submitted") return;

    const receiptNumber = this._getReceiptNumber();
    const receiptDate = this._getReceiptDate();
    if (receiptNumber === "" || receiptDate === "") {
      this._showReceiptError("Renseignez le numéro et la date d'accusé avant d'enregistrer.");
      return;
    }

    if (!window.confirm(
      "Enregistrer cet accusé de réception ? "
        + "Un récapitulatif immuable sera archivé dans votre coffre.",
    )) {
      return;
    }

    this._submitInFlight = true;
    this.saveReceiptButtonTarget.disabled = true;

    try {
      const response = await api.urssaf.submit(this._declaration.id, {
        receiptNumber,
        receiptDate: new Date(receiptDate).toISOString(),
      });
      this._declaration = response.declaration ?? response;
      this._renderSubmissionState();
      this._renderCaTable();
      await this.loadHistory(1);
      this.showSuccess("Accusé enregistré et récapitulatif archivé au coffre.");
    } catch (error) {
      if (error instanceof APIError && error.status === 409) {
        this.showError("Cette déclaration a déjà été enregistrée.");
        await this.loadCurrent();
      } else if (error instanceof APIError && error.status === 423) {
        this.showError("Cette période est verrouillée et ne peut plus être modifiée.");
      } else if (error instanceof APIError && error.status === 422) {
        this._showReceiptError(this._extractMessage(error, "Numéro ou date d'accusé invalide."));
      } else {
        this.showError(this._extractMessage(error, "L'enregistrement de l'accusé a échoué."));
      }
    } finally {
      this._submitInFlight = false;
      this.validateReceipt();
    }
  }

  _getReceiptNumber() {
    return this.hasReceiptNumberInputTarget
      ? (this.receiptNumberInputTarget.value ?? "").trim()
      : "";
  }

  _getReceiptDate() {
    return this.hasReceiptDateInputTarget
      ? (this.receiptDateInputTarget.value ?? "").trim()
      : "";
  }

  _showReceiptError(message) {
    if (!this.hasReceiptErrorTarget) return;
    this.receiptErrorTarget.textContent = message;
    this.receiptErrorTarget.hidden = false;
  }

  _hideReceiptError() {
    if (this.hasReceiptErrorTarget) this.receiptErrorTarget.hidden = true;
  }

  _renderSubmissionState() {
    const d = this._declaration;
    const isSubmitted = d?.submissionStatus === "submitted";

    this.submitDraftStateTarget.hidden = isSubmitted;
    this.submitDoneStateTarget.hidden = !isSubmitted;

    // Lien de téléchargement du récap PDF (dispo avant soumission).
    if (this.hasRecapDownloadLinkTarget && d?.id) {
      this.recapDownloadLinkTarget.setAttribute(
        "href",
        `/urssaf/declaration/${encodeURIComponent(d.id)}/pdf`,
      );
    }

    if (isSubmitted) {
      this.receiptNumberTarget.textContent = d.urssafReceiptNumber ?? "—";
      const ts = d.urssafReceiptTimestamp ?? d.submittedAt;
      if (ts) {
        this.receiptTimestampTarget.setAttribute("datetime", ts);
        this.receiptTimestampTarget.textContent = this._formatLongDateTime(ts);
      } else {
        this.receiptTimestampTarget.textContent = "—";
      }
      this.receiptCaTotalTarget.textContent = this._formatEuro(this._computeTotalCaDeclarable());
      const cot = d.cotisationsAmount ?? d.estimatedCotisationsAmount;
      this.receiptCotisationsTarget.textContent = cot != null ? this._formatEuro(Number(cot)) : "—";
      this.receiptVaultLinkTarget.href = d.vaultDocumentId
        ? `/coffre.html?document=${encodeURIComponent(d.vaultDocumentId)}`
        : "#";
      if (!d.vaultDocumentId) {
        this.receiptVaultLinkTarget.setAttribute("aria-disabled", "true");
        this.receiptVaultLinkTarget.style.pointerEvents = "none";
        this.receiptVaultLinkTarget.style.opacity = "0.5";
      }
    } else {
      this.validateReceipt();
    }
  }

  // ---------------------------------------------------------------------------
  // Historique paginé
  // ---------------------------------------------------------------------------

  async loadHistory(page) {
    try {
      const response = await api.urssaf.listDeclarations(page);
      const items = response.items ?? response.declarations ?? [];
      const meta = response.pagination ?? response.meta ?? {};
      const total = Number(meta.total ?? items.length);
      const pageSize = Number(meta.pageSize ?? HISTORY_PAGE_SIZE);
      this._historyPage = Number(meta.page ?? page);
      this._historyTotalPages = Math.max(1, Math.ceil(total / pageSize));
      this._renderHistory(items, total, pageSize);
    } catch (error) {
      this.historyTableBodyTarget.innerHTML = "";
      this.historyTableBodyTarget.appendChild(
        this.cloneTemplate("urssaf-history-error-template"),
      );
      this.historyPaginationTarget.hidden = true;
    }
  }

  _renderHistory(items, total, pageSize) {
    this.historyTableBodyTarget.innerHTML = "";

    if (!items.length) {
      this.historyTableBodyTarget.appendChild(
        this.cloneTemplate("urssaf-history-empty-template"),
      );
      this.historyPaginationTarget.hidden = true;
      return;
    }

    for (const item of items) {
      const fragment = this.cloneTemplate("urssaf-history-row-template");
      const caTotal = Number(item.caTotalAmount ?? item.totalCa ?? 0);
      const cot = item.cotisationsAmount ?? item.estimatedCotisationsAmount;
      this.fillSlots(fragment, {
        period: this._formatPeriodLabel(item),
        caTotal: this._formatEuro(caTotal),
        receiptNumber: item.urssafReceiptNumber ?? "—",
        cotisations: cot != null ? this._formatEuro(Number(cot)) : "—",
      });

      const statusCell = fragment.querySelector('[data-slot="status"]');
      if (statusCell) {
        statusCell.removeAttribute("data-slot");
        statusCell.appendChild(this._buildStatusPill(item.submissionStatus));
      }
      const vaultCell = fragment.querySelector('[data-slot="vaultLink"]');
      if (vaultCell) {
        vaultCell.removeAttribute("data-slot");
        vaultCell.appendChild(this._buildVaultLink(item));
      }
      this.historyTableBodyTarget.appendChild(fragment);
    }

    const start = (this._historyPage - 1) * pageSize + 1;
    const end = Math.min(this._historyPage * pageSize, total);
    this.historyPageInfoTarget.textContent = `${start} — ${end} sur ${total} déclaration${total > 1 ? "s" : ""}`;
    this._renderPagination();
    this.historyPaginationTarget.hidden = false;
  }

  _renderPagination() {
    this.historyPagesContainerTarget.innerHTML = "";
    const pages = this.getPagesToShow(this._historyPage, this._historyTotalPages);

    const prev = this._buildPaginationButton("‹", this._historyPage - 1, this._historyPage <= 1);
    prev.setAttribute("aria-label", "Page précédente");
    this.historyPagesContainerTarget.appendChild(prev);

    for (const page of pages) {
      if (page === "...") {
        const span = document.createElement("span");
        span.textContent = "…";
        span.className = "is-muted";
        this.historyPagesContainerTarget.appendChild(span);
        continue;
      }
      const btn = this._buildPaginationButton(String(page), page, false);
      btn.setAttribute("aria-label", `Page ${page}`);
      if (page === this._historyPage) {
        btn.classList.add("is-current");
        btn.setAttribute("aria-current", "page");
      }
      this.historyPagesContainerTarget.appendChild(btn);
    }

    const next = this._buildPaginationButton("›", this._historyPage + 1, this._historyPage >= this._historyTotalPages);
    next.setAttribute("aria-label", "Page suivante");
    this.historyPagesContainerTarget.appendChild(next);
  }

  _buildPaginationButton(label, page, disabled) {
    const fragment = this.cloneTemplate("urssaf-pagination-button-template");
    const btn = fragment.querySelector("button");
    btn.removeAttribute("data-slot");
    btn.textContent = label;
    btn.disabled = disabled;
    if (!disabled) {
      btn.addEventListener("click", () => this.loadHistory(page));
    }
    return btn;
  }

  // ---------------------------------------------------------------------------
  // Navigation entre périodes — réservé V1.1 (boutons inactifs)
  // ---------------------------------------------------------------------------

  previousPeriod() {
    // V1 : navigation inter-périodes non livrée (cf. spec 20.3).
  }

  nextPeriod() {
    // V1 : navigation inter-périodes non livrée (cf. spec 20.3).
  }

  // ---------------------------------------------------------------------------
  // Helpers UI : pills, vault link
  // ---------------------------------------------------------------------------

  _buildStatusPill(status) {
    const span = document.createElement("span");
    span.className = status === "submitted"
      ? "nm-pill nm-pill--olive"
      : status === "failed"
        ? "nm-pill nm-pill--alert"
        : "nm-pill";
    const dot = document.createElement("span");
    dot.className = "nm-pill__dot";
    dot.setAttribute("aria-hidden", "true");
    span.appendChild(dot);
    span.appendChild(document.createTextNode(SUBMISSION_STATUS_LABELS[status] ?? status ?? "—"));
    return span;
  }

  _buildVaultLink(item) {
    if (item.vaultDocumentId) {
      const a = document.createElement("a");
      a.href = `/coffre.html?document=${encodeURIComponent(item.vaultDocumentId)}`;
      a.className = "nm-btn nm-btn--link nm-btn--sm";
      a.textContent = "Coffre";
      return a;
    }
    const span = document.createElement("span");
    span.className = "is-muted fs-12";
    span.textContent = "—";
    return span;
  }

  // ---------------------------------------------------------------------------
  // Helpers métier : période, CA total, draft sessionStorage
  // ---------------------------------------------------------------------------

  _computeTotalCaDeclarable() {
    const caByCategory = this._declaration?.caByActivityCategory ?? {};
    const adjustmentsByCategory = this._declaration?.manualCaAdjustmentByCategory ?? {};
    let total = 0;
    for (const category of CATEGORY_ORDER) {
      const ca = Number(caByCategory[category] ?? 0);
      const adj = Number(adjustmentsByCategory[category] ?? 0);
      total += ca + adj;
    }
    return total;
  }

  _buildDeclarableCaByCategory() {
    const caByCategory = this._declaration?.caByActivityCategory ?? {};
    const adjustmentsByCategory = this._declaration?.manualCaAdjustmentByCategory ?? {};
    const out = {};
    for (const category of CATEGORY_ORDER) {
      const ca = Number(caByCategory[category] ?? 0);
      const adj = Number(adjustmentsByCategory[category] ?? 0);
      if (caByCategory[category] != null) {
        out[category] = ca + adj;
      }
    }
    return out;
  }

  _formatPeriodLabel(d) {
    if (!d) return "—";
    if (d.periodLabel) {
      return this._humanizePeriodLabel(d.periodLabel);
    }
    if (d.periodStartDate) {
      const date = new Date(d.periodStartDate);
      if (d.periodType === "trimestrielle") {
        const q = Math.floor(date.getMonth() / 3) + 1;
        return `T${q} ${date.getFullYear()}`;
      }
      return date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }).replace(/^./, (c) => c.toUpperCase());
    }
    return "—";
  }

  _humanizePeriodLabel(label) {
    if (/^\d{4}-T[1-4]$/.test(label)) {
      const [year, q] = label.split("-T");
      return `T${q} ${year}`;
    }
    if (/^\d{4}-\d{2}$/.test(label)) {
      const [year, month] = label.split("-");
      const date = new Date(Number(year), Number(month) - 1, 1);
      return date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }).replace(/^./, (c) => c.toUpperCase());
    }
    return label;
  }

  _adjustmentDraftKey(category) {
    const orgId = this._declaration?.organizationId ?? "self";
    const declId = this._declaration?.id ?? "current";
    return `nm.urssaf.adjustment.${orgId}.${declId}.${category}`;
  }

  _readAdjustmentDraft(category) {
    try {
      const raw = sessionStorage.getItem(this._adjustmentDraftKey(category));
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  _writeAdjustmentDraft(category, draft) {
    try {
      sessionStorage.setItem(this._adjustmentDraftKey(category), JSON.stringify(draft));
    } catch (e) {
      // sessionStorage indisponible (mode privé strict) — silencieux.
    }
  }

  _clearAdjustmentDraft(category) {
    try {
      sessionStorage.removeItem(this._adjustmentDraftKey(category));
    } catch (e) {
      // idem
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers d'erreurs et de format
  // ---------------------------------------------------------------------------

  _showErrorBanner(title, message) {
    this.errorBannerTitleTarget.textContent = title;
    this.errorBannerMessageTarget.textContent = message;
    this.errorBannerTarget.hidden = false;
  }

  _hideErrorBanner() {
    this.errorBannerTarget.hidden = true;
  }

  _showAdjustmentNoteError(message) {
    this.adjustmentNoteErrorTarget.textContent = message;
    this.adjustmentNoteErrorTarget.hidden = false;
  }

  _clearAdjustmentNoteError() {
    this.adjustmentNoteErrorTarget.hidden = true;
  }

  _handleLoadError(error) {
    if (error instanceof APIError && error.status === 503) {
      this._showErrorBanner(
        "Le service URSSAF est temporairement indisponible.",
        "Impossible de charger la déclaration courante. Réessayez dans quelques minutes.",
      );
      return;
    }
    this.showError(this._extractMessage(error, "Impossible de charger la déclaration URSSAF."));
  }

  _extractMessage(error, fallback) {
    if (error instanceof APIError) {
      return error.message || fallback;
    }
    return error?.message || fallback;
  }

  _parseAmount(value) {
    if (typeof value !== "string") return Number(value);
    const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
    if (normalized === "") return 0;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  _formatAmountInput(amount) {
    return amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  _formatEuroValue(amount) {
    return amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  _formatEuro(amount, signed = false) {
    const formatted = this._formatEuroValue(Math.abs(amount));
    if (signed && amount > 0) return `+${formatted} €`;
    if (signed && amount < 0) return `−${formatted} €`;
    return `${this._formatEuroValue(amount)} €`;
  }

  _formatRate(rate) {
    const numeric = Number(rate);
    if (!Number.isFinite(numeric)) return "—";
    const pct = numeric <= 1 ? numeric * 100 : numeric;
    return `${pct.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %`;
  }

  _formatLongDate(iso) {
    try {
      return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch (e) {
      return iso;
    }
  }

  _formatLongDateTime(iso) {
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
    } catch (e) {
      return iso;
    }
  }

}
